import React, { useMemo, useCallback } from 'react'
import { Formik } from 'formik'
import moment from 'moment'
import {
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from 'reactstrap'
import { licenseSchema } from 'lib/schemas'
import ErrorBoundary from 'components/ErrorBoundary'
import AssigneeTab from './components/AssigneeTab'
import NetSuiteAssetDetails from './components/NetSuiteAssetDetails'

interface EditLicenseModalProps {
  isOpen: boolean
  toggle: () => void
  // Container props
  currentSchool: any
  selectedNetSuiteAsset: any
  selectedSites: any[]
  selectedSitesUsers: any[]
  schoolSelected: (formik: any, site: any) => Promise<void>
  customerIdValidateAsync: (value: string, formik: any) => Promise<string>
  handleSubmit: (values: any, { setSubmitting }: any) => Promise<void>
  schoolLookupIsValid: (formikProps: any) => boolean
  initialSiteData?: any
  isInitialLoad?: boolean
}

export const EditLicenseModal: React.FC<EditLicenseModalProps> = ({
  currentSchool,
  customerIdValidateAsync,
  handleSubmit,
  initialSiteData,
  isInitialLoad = false,
  isOpen,
  schoolLookupIsValid,
  schoolSelected,
  selectedNetSuiteAsset,
  selectedSites,
  selectedSitesUsers,
  toggle,
}) => {
  const initialValues = useMemo(() => {
    if (!selectedNetSuiteAsset || !selectedNetSuiteAsset.product) {
      return {}
    }

    return {
      selectedNetSuiteAsset,
      // Flatten the site object to top-level fields
      nsCustomerId: currentSchool.nsCustomerId || '',
      netsuiteCustomerId: currentSchool.netsuiteCustomerId,
      city: currentSchool.city || '',
      country: currentSchool.country || '',
      region: currentSchool.region || '',
      companyName: currentSchool.companyName || '',
      organizationName: currentSchool.organizationName || '',
      cancelledDate: selectedNetSuiteAsset.cancelledDate
        ? moment(selectedNetSuiteAsset.cancelledDate).format('YYYY-MM-DD')
        : '',
      isCanceled: !!selectedNetSuiteAsset.cancelledDate,
      siteChangeChecked: false,
      siteWasChanged: false,
    }
  }, [selectedNetSuiteAsset, currentSchool])

  const handleFormSubmit = (values: any, { setSubmitting }: any) =>
    handleSubmit(values, { setSubmitting })

  const handleSchoolSelected = useCallback(
    (site: any) => (formik: any) => schoolSelected(formik, site),
    [schoolSelected],
  )

  const handleCustomerIdValidate = useCallback(
    (value: string) => (formik: any) => customerIdValidateAsync(value, formik),
    [customerIdValidateAsync],
  )

  if (!selectedNetSuiteAsset || !selectedNetSuiteAsset.product) return null

  return (
    <Modal
      backdrop="static"
      centered
      fade
      isOpen={isOpen}
      size="lg"
      toggle={toggle}
    >
      <ModalHeader toggle={toggle}>
        License Edit&nbsp;<Badge>SS: {selectedNetSuiteAsset.id}</Badge>
        <Badge>NS: {selectedNetSuiteAsset.nsId}</Badge>
      </ModalHeader>

      <ErrorBoundary>
        <Formik
          enableReinitialize={true}
          initialValues={initialValues}
          isInitialValid={true}
          onSubmit={handleFormSubmit}
          validationSchema={licenseSchema}
        >
          {formikProps => (
            <form onSubmit={formikProps.handleSubmit}>
              <ModalBody>
                <NetSuiteAssetDetails
                  selectedNetSuiteAsset={selectedNetSuiteAsset}
                />
                <AssigneeTab
                  formikProps={formikProps}
                  selectedNetSuiteAsset={selectedNetSuiteAsset}
                  selectedSites={selectedSites}
                  selectedSitesUsers={selectedSitesUsers}
                  onSchoolSelected={handleSchoolSelected}
                  onCustomerIdValidate={handleCustomerIdValidate}
                  initialSiteData={initialSiteData}
                  isInitialLoad={isInitialLoad}
                />
              </ModalBody>
              <ModalFooter>
                <Button
                  className="btn-fixed-width"
                  color="link"
                  disabled={formikProps.isSubmitting}
                  onClick={toggle}
                  style={{
                    fontSize: '18px',
                    fontFamily: 'Molde-Medium',
                    color: '#5F5F5F',
                    margin: '12px 0px auto',
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="btn-fixed-width"
                  color={formikProps.isSubmitting ? 'disabled' : 'primary'}
                  disabled={
                    formikProps.isSubmitting ||
                    formikProps.isValidating ||
                    !formikProps.isValid ||
                    !formikProps.touched ||
                    !schoolLookupIsValid(formikProps)
                  }
                  style={{
                    margin: '12px 4px 24px 48px',
                    width: '130px',
                    height: '42px',
                    fontSize: '18px',
                    fontFamily: 'Molde-Medium',
                  }}
                  type="submit"
                >
                  Submit
                </Button>
              </ModalFooter>
            </form>
          )}
        </Formik>
      </ErrorBoundary>
    </Modal>
  )
}
